import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { Footer } from '@/components/layout/Footer';

const Privacy: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { t } = useTranslation('common');
  const code = (lang || 'en').split('-')[0];

  return (
    <AppLayout>
      <Helmet>
        <title>Privacy Policy | JiGSO EX Hub</title>
        <meta name="description" content="Privacy Policy for JiGSO EX Hub - Learn how we collect, use, and protect your data in compliance with GDPR and privacy regulations." />
        <link rel="canonical" href={`${window.location.origin}/${code}/privacy`} />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-background via-secondary/20 to-accent/20 pt-16 pb-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Privacy Policy — JiGSO EX Hub
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Your privacy is important to us. Learn how we collect, use, and protect your data.
              </p>
              <div className="mt-6 text-sm text-muted-foreground">
                <strong>Effective date:</strong> September 2024
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none">
                <div className="space-y-8">
                  {/* Who We Are */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">1. Who We Are</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      JiGSO EX Hub ("EX Hub", "we", "us", "our") is a product of <strong>JiGSO BVBA</strong>, a company registered in Belgium.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      We provide digital HR tools and resources to help organizations improve employee experience.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      This Privacy Policy applies to our platform at <a href="https://exhub.jigso.com" className="text-primary hover:underline">https://exhub.jigso.com</a>.
                    </p>
                    <div className="bg-muted/50 rounded-lg p-4 mt-4">
                      <p className="text-sm font-semibold mb-2">Data Controller:</p>
                      <p className="text-sm text-muted-foreground">JiGSO BV</p>
                      <p className="text-sm text-muted-foreground">Belgium</p>
                      <p className="text-sm text-muted-foreground">Website: <a href="https://jigso.com" className="text-primary hover:underline">https://jigso.com</a></p>
                      <p className="text-sm text-muted-foreground">Email: <a href="mailto:privacy@jigso.com" className="text-primary hover:underline">privacy@jigso.com</a></p>
                    </div>
                  </div>

                  {/* Data We Collect */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">2. Data We Collect</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-3">a) Analytics Data (Plausible Analytics)</h3>
                        <p className="text-muted-foreground leading-relaxed mb-3">
                          We use <a href="https://plausible.io/" className="text-primary hover:underline">Plausible Analytics</a> to understand how visitors use our platform.
                        </p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                          <li><strong>No cookies are used.</strong></li>
                          <li>Data is collected in an <strong>aggregated, anonymized form</strong> (no personal identifiers such as IP addresses, unique IDs, or device fingerprints).</li>
                          <li>Examples of collected data: page views, referring websites, device type, and approximate location (country-level).</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-3">b) Email Addresses for Generated Templates</h3>
                        <p className="text-muted-foreground leading-relaxed mb-3">
                          When you use our template generation tools, we ask for your email address before showing your results. We use it to:
                        </p>
                        <ol className="list-decimal list-inside text-muted-foreground space-y-1 ml-4">
                          <li>Send your generated results</li>
                          <li>Occasionally follow up with friendly check-ins or feedback requests</li>
                        </ol>
                        <p className="text-muted-foreground leading-relaxed mt-3">
                          We do <strong>not</strong> send unsolicited marketing emails and we do <strong>not</strong> sell or share your email with third parties for marketing purposes.
                        </p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-3">c) Account Data (Optional)</h3>
                        <p className="text-muted-foreground leading-relaxed mb-3">
                          If you choose to create a <strong>JiGSO EX Hub account</strong>:
                        </p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                          <li>We collect your email, password (securely hashed), and any templates or history you save.</li>
                          <li>This enables features like template history, analytics, and personalization.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* How We Use Your Data */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Data</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      We process your data to:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Provide you with template results and platform features</li>
                      <li>Improve the EX Hub experience through aggregated analytics</li>
                      <li>Maintain your account and saved content</li>
                      <li>Communicate about important updates or feedback requests</li>
                    </ul>
                  </div>

                  {/* Legal Basis for Processing */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">4. Legal Basis for Processing</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      Under GDPR, we process personal data based on:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li><strong>Performance of a contract</strong> (providing generated results, maintaining your account)</li>
                      <li><strong>Legitimate interests</strong> (aggregated analytics for service improvement)</li>
                      <li><strong>Consent</strong> (optional communications beyond service delivery)</li>
                    </ul>
                  </div>

                  {/* Data Retention */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li><strong>Email addresses</strong>: retained only as long as needed to provide results or maintain your account.</li>
                      <li><strong>Analytics data</strong>: aggregated, no personal identifiers stored.</li>
                    </ul>
                  </div>

                  {/* Your Rights (GDPR) */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">6. Your Rights (GDPR)</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      You have the right to:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Access, correct, or delete your personal data</li>
                      <li>Withdraw consent at any time</li>
                      <li>Request data portability</li>
                      <li>Lodge a complaint with your local Data Protection Authority</li>
                    </ul>
                  </div>

                  {/* Security */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">7. Security</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We use encrypted connections, secure data storage, and strict access control to protect your data.
                    </p>
                  </div>

                  {/* Third Parties */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">8. Third Parties</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      We do not share your personal data with third parties except:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li><strong>Supabase</strong> (authentication, database services)</li>
                      <li><strong>AWS/Vercel</strong> (hosting)</li>
                      <li><strong>Plausible Analytics</strong> (aggregated analytics only)</li>
                    </ul>
                    <p className="text-muted-foreground leading-relaxed mt-3">
                      All third-party services comply with GDPR or equivalent standards.
                    </p>
                  </div>

                  {/* Contact Us */}
                  <div className="bg-card border rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      For privacy-related questions, contact:
                    </p>
                    <p className="text-lg font-semibold">
                      <a href="mailto:privacy@jigso.com" className="text-primary hover:underline">privacy@jigso.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default Privacy; 